import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from '../../lib/queryClient';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { ClientWithProjects } from '../../hooks/use-clients';
import { 
  Edit, 
  FileText, 
  Phone, 
  Mail, 
  MapPin, 
  User, 
  Trash2, 
  Plus, 
  Share2, 
  Copy,
  AlertCircle, 
  ExternalLink, 
  FileSignature, 
  ClipboardCheck,
  Shield,
  Eye
} from "lucide-react";
import { formatCurrency, formatDate } from '../../lib/utils';
import { formatPhone } from '../../lib/validation';
import ProjectForm, { ProjectInput } from '../projects/project-form';
import { useProjects } from '../../hooks/use-projects';
import { Project } from '../../hooks/use-clients';
import { ProjectInsert } from "../../../../shared/schema";


type ClientDetailProps = {
  client: ClientWithProjects;
  onEdit: () => void;
  onDelete: () => void;
  onNewEstimate: () => void;
};

export default function ClientDetail({ 
  client, 
  onEdit, 
  onDelete, 
  onNewEstimate 
}: ClientDetailProps) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { toast } = useToast();
  const portalLinkRef = useRef<HTMLInputElement>(null);
  const [portalUrl, setPortalUrl] = useState<string>('');
  const [showPortalUrl, setShowPortalUrl] = useState(false);
  const queryClient = useQueryClient();
  
  // Note log state
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [noteType, setNoteType] = useState('general');
  
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  };

  const getProjectStatusClass = (status: string) => {
    switch (status) {
      case "completed":
      case "Completed":
        return "bg-green-100 text-green-800 hover:bg-green-100";
      case "in_progress":
      case "In Progress":
        return "bg-blue-100 text-blue-800 hover:bg-blue-100";
      case "on_hold":
      case "On Hold":
        return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
      case "cancelled":
      case "Cancelled":
        return "bg-red-100 text-red-800 hover:bg-red-100";
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-100";
    }
  };
  
  // Generate secure portal URL with token
  const getClientPortalUrl = async () => {
    try {
      // Get contractor ID from user data
      const userData = localStorage.getItem('user');
      let contractorId = '';
      
      if (userData) {
        try {
          const user = JSON.parse(userData);
          contractorId = user.id?.toString() || '';
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }
      
      if (!contractorId) {
        console.error('No contractor ID found in user data');
        return null;
      }
      
      const response = await fetch('/api/generate-portal-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          ...(contractorId != null && { 'X-Contractor-ID': String(contractorId) }),
        },
        body: JSON.stringify({ clientId: client.id })
      });

      if (response.ok) {
        const data = await response.json();
        return data.portalUrl;
      } else {
        const errorData = await response.json();
        console.error('Failed to generate portal token:', errorData);
        return null;
      }
    } catch (error) {
      console.error('Error generating portal URL:', error);
      return null;
    }
  };
  
  // Function to copy portal link to clipboard
  const copyPortalLink = async () => {
    const url = await getClientPortalUrl();
    if (!url) {
      toast({
        title: "Error",
        description: "Failed to generate secure portal link.",
        variant: "destructive",
      });
      return;
    }

    setPortalUrl(url);
    setShowPortalUrl(true);

    // Try to copy to clipboard
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        toast({
          title: "Link copied",
          description: "The secure portal link has been copied to clipboard.",
        });
      } catch (err) {
        console.error("Error copying to clipboard:", err);
        // Fallback: show input field for manual copy
        toast({
          title: "Select the link below",
          description: "Tap the link field to select and copy it manually.",
        });
      }
    } else {
      // Fallback for browsers without clipboard API
      toast({
        title: "Select the link below",
        description: "Tap the link field to select and copy it manually.",
      });
    }
  };
  
  // Función para abrir el portal del cliente en una nueva pestaña
  const openClientPortal = async () => {
    const url = await getClientPortalUrl();
    if (url) {
      setPortalUrl(url);
      setShowPortalUrl(true);
      
      // Detect if mobile device
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
      
      if (isMobile) {
        // On mobile, use window.location to avoid popup blockers
        // Show toast first, then navigate
        toast({
          title: "Opening Client Portal",
          description: "Redirecting to client portal...",
        });
        // Small delay to show toast, then navigate
        setTimeout(() => {
          window.location.href = url;
        }, 300);
      } else {
        // On desktop, try to open in new window/tab
        try {
          const newWindow = window.open(url, '_blank');
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            // Popup blocked - show URL for manual navigation
            toast({
              title: "Popup blocked",
              description: "Please use the link below to open the portal.",
            });
          }
        } catch (err) {
          console.error("Error opening portal:", err);
          toast({
            title: "Please use the link below",
            description: "Click the link to open the client portal.",
          });
        }
      }
    } else {
      toast({
        title: "Error",
        description: "Failed to generate secure portal link.",
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Fetch client notes
  const { data: clientNotes = [], isLoading: isLoadingNotes } = useQuery({
    queryKey: [`/api/protected/client-notes`, client.id],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/protected/client-notes?clientId=${client.id}`);
      return response.json();
    }
  });

  // Add note mutation
  const addNoteMutation = useMutation({
    mutationFn: async (noteData: { clientId: number; userName: string; noteText: string; noteType: string }) => {
      const response = await apiRequest('POST', '/api/protected/client-notes', noteData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/protected/client-notes`, client.id] });
      setNewNoteText('');
      setNoteType('general');
      setIsAddingNote(false);
      toast({
        title: "Note Added",
        description: "Activity note has been saved successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add note. Please try again.",
        variant: "destructive",
      });
    }
  });

  const handleAddNote = () => {
    if (!newNoteText.trim()) {
      toast({
        title: "Error",
        description: "Note text cannot be empty",
        variant: "destructive",
      });
      return;
    }

    // Get user name from auth context or localStorage
    const userName = localStorage.getItem('userName') || 'User';
    
    addNoteMutation.mutate({
      clientId: client.id,
      userName,
      noteText: newNoteText.trim(),
      noteType
    });
  };

  // Calcular total de ingresos de todos los proyectos
  const totalRevenue = client.projects && client.projects.length > 0
    ? client.projects.reduce((total, project) => {
        const budget = typeof project.budget === 'string' 
          ? parseFloat(project.budget.replace(/[$,]/g, ''))
          : (project.budget || 0);
        return total + budget;
      }, 0)
    : 0;

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    onDelete();
    setIsDeleteDialogOpen(false);
  };
  
  // Estado y manejo para el formulario de nuevo proyecto
  const [isProjectFormOpen, setIsProjectFormOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const { createProject, updateProject, deleteProject, isCreating, isUpdating } = useProjects();
  
  const handleAddProject = () => {
    setSelectedProject(null);
    setIsProjectFormOpen(true);
  };
  
  const handleEditProject = (project: Project) => {
    setSelectedProject(project);
    setIsProjectFormOpen(true);
  };
  
  const handleProjectFormSubmit = (data: ProjectInput & { clientId: number }) => {
    if (selectedProject) {
      updateProject({
        id: selectedProject.id,
        data: {
          ...data,
          contractorId: 1 // Estamos usando el ID 1 para el contratista logueado
        }
      }, {
        onSuccess: () => {
          // Cerrar el formulario después de actualizar exitosamente
          setIsProjectFormOpen(false);
          setSelectedProject(null);
        }
      });
    } else {
      // Asegurarse de que el objeto tenga todos los campos requeridos
      const projectData: ProjectInsert = {
        ...data,
        contractorId: 1, // ID del contratista logueado
        status: data.status || "pending"
      };
      createProject(projectData, {
        onSuccess: () => {
          // Cerrar el formulario después de crear exitosamente
          setIsProjectFormOpen(false);
          setSelectedProject(null);
        }
      });
    }
  };

  return (
    <div className="space-y-6 bg-white dark:bg-slate-900 p-6 rounded-xl">
      <div className="flex items-center mb-6">
        <Avatar className="h-16 w-16 mr-4">
          <AvatarFallback className="text-lg bg-gradient-to-r from-green-400 to-green-600 text-white">
            {getInitials(client.firstName, client.lastName)}
          </AvatarFallback>
        </Avatar>
        <div>
          <h4 className="text-lg font-semibold text-slate-800 dark:text-slate-200">{client.firstName} {client.lastName}</h4>
          <p className="text-slate-600 dark:text-slate-400">{formatPhone(client.phone)}</p>
          <p className="text-slate-600 dark:text-slate-400">{client.email}</p>
        </div>
      </div>
      
      <Tabs defaultValue="info">
        <TabsList className="mb-4 grid w-full grid-cols-4 bg-slate-200 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl p-2 gap-2">
          <TabsTrigger 
            value="info" 
            className="px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-green-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=inactive]:text-slate-700 dark:data-[state=inactive]:text-slate-300 data-[state=inactive]:hover:text-green-600 dark:data-[state=inactive]:hover:text-green-400 data-[state=inactive]:hover:bg-slate-300 dark:data-[state=inactive]:hover:bg-slate-700"
          >
            Contact Information
          </TabsTrigger>
          <TabsTrigger 
            value="projects" 
            className="px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-green-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=inactive]:text-slate-700 dark:data-[state=inactive]:text-slate-300 data-[state=inactive]:hover:text-green-600 dark:data-[state=inactive]:hover:text-green-400 data-[state=inactive]:hover:bg-slate-300 dark:data-[state=inactive]:hover:bg-slate-700"
          >
            Projects
          </TabsTrigger>
          <TabsTrigger 
            value="notes" 
            className="px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-green-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=inactive]:text-slate-700 dark:data-[state=inactive]:text-slate-300 data-[state=inactive]:hover:text-green-600 dark:data-[state=inactive]:hover:text-green-400 data-[state=inactive]:hover:bg-slate-300 dark:data-[state=inactive]:hover:bg-slate-700"
          >
            Notes
          </TabsTrigger>
          <TabsTrigger 
            value="portal" 
            className="px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-green-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=inactive]:text-slate-700 dark:data-[state=inactive]:text-slate-300 data-[state=inactive]:hover:text-green-600 dark:data-[state=inactive]:hover:text-green-400 data-[state=inactive]:hover:bg-slate-300 dark:data-[state=inactive]:hover:bg-slate-700"
          >
            Client Portal
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="info" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="remodra-card p-6">
              <h5 className="font-medium text-green-500 mb-4 flex items-center gap-2">
                <User className="h-5 w-5" />
                Contact Information
              </h5>
              <div className="space-y-3 text-sm">
                {(client.address || client.city || client.state) && (
                  <p className="flex items-center">
                    <MapPin className="text-slate-500 dark:text-slate-400 mr-3 h-4 w-4" />
                    <span className="text-slate-700 dark:text-slate-200">
                      {client.address && `${client.address}, `}
                      {client.city && `${client.city}, `}
                      {client.state && `${client.state}`}
                      {client.zip && ` ${client.zip}`}
                    </span>
                  </p>
                )}
                {client.phone && (
                  <p className="flex items-center">
                    <Phone className="text-slate-500 dark:text-slate-400 mr-3 h-4 w-4" />
                    <span className="text-slate-700 dark:text-slate-200">{formatPhone(client.phone)}</span>
                  </p>
                )}
                {client.email && (
                  <p className="flex items-center">
                    <Mail className="text-slate-500 dark:text-slate-400 mr-3 h-4 w-4" />
                    <span className="text-slate-700 dark:text-slate-200">{client.email}</span>
                  </p>
                )}
                <p className="flex items-center">
                  <User className="text-slate-500 dark:text-slate-400 mr-3 h-4 w-4" />
                  <span className="text-slate-700 dark:text-slate-200">Client since: {formatDate(client.createdAt)}</span>
                </p>
              </div>
            </div>
            
            <div className="remodra-card p-6">
              <h5 className="font-medium text-green-500 mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Summary
              </h5>
              <div className="space-y-3 text-sm">
                <p className="flex items-center justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Projects</span>
                  <span className="font-medium text-slate-800 dark:text-slate-200">{client.projects ? client.projects.length : 0}</span>
                </p>
                <p className="flex items-center justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Completed</span>
                  <span className="font-medium text-slate-800 dark:text-slate-200">
                    {client.projects ? client.projects.filter(p => 
                      p.status === "completed" || p.status === "Completed"
                    ).length : 0}
                  </span>
                </p>
                <p className="flex items-center justify-between">
                  <span className="text-slate-600 dark:text-slate-400">In Progress</span>
                  <span className="font-medium text-slate-800 dark:text-slate-200">
                    {client.projects ? client.projects.filter(p => 
                      p.status === "in_progress" || p.status === "In Progress"
                    ).length : 0}
                  </span>
                </p>
                <p className="flex items-center justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Total Revenue</span>
                  <span className="font-medium text-slate-800 dark:text-slate-200">{formatCurrency(totalRevenue)}</span>
                </p>
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="projects" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h5 className="font-medium text-green-500 flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Projects
            </h5>
            <Button 
              size="sm" 
              variant="outline" 
              className="remodra-button-outline flex items-center"
              onClick={handleAddProject}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Project
            </Button>
          </div>
          
          {!client.projects || client.projects.length === 0 ? (
            <div className="text-center py-6 border border-dashed border-slate-300 dark:border-slate-600 rounded-md bg-slate-100 dark:bg-slate-800/50">
              <p className="text-slate-600 dark:text-slate-400">No projects for this client yet.</p>
              <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">Use "New Project" button above or create an estimate first.</p>
              <div className="flex justify-center mt-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="remodra-button-outline flex items-center"
                  onClick={onNewEstimate}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Create Estimate
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {client.projects.map((project) => (
                <div 
                  key={project.id} 
                  className="remodra-card p-4 hover:shadow-lg transition-all duration-200 cursor-pointer border border-slate-300 dark:border-slate-600"
                  onClick={() => handleEditProject(project)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h6 className="font-medium text-lg text-slate-800 dark:text-slate-200">{project.title}</h6>
                      {project.description && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">{project.description}</p>
                      )}
                    </div>
                    <Badge className="remodra-badge">
                      {typeof project.budget === 'number' 
                        ? formatCurrency(project.budget)
                        : (project.budget ? formatCurrency(parseFloat(project.budget.toString())) : '-')}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center mt-3 text-sm text-slate-600 dark:text-slate-400">
                    <div className="flex items-center">
                      <span className="capitalize text-slate-700 dark:text-slate-300">
                        {project.status === "in_progress" && "In Progress"}
                        {project.status === "In Progress" && "In Progress"}
                        {project.status === "completed" && "Completed"}
                        {project.status === "Completed" && "Completed"}
                        {project.status === "on_hold" && "On Hold"}
                        {project.status === "On Hold" && "On Hold"}
                        {project.status === "cancelled" && "Cancelled"}
                        {project.status === "Cancelled" && "Cancelled"}
                        {!["in_progress", "In Progress", "completed", "Completed", "on_hold", "On Hold", "cancelled", "Cancelled"].includes(project.status) && project.status}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4">
                      {project.startDate && (
                        <span>Start: {formatDate(project.startDate)}</span>
                      )}
                      {project.endDate && (
                        <span>End: {formatDate(project.endDate)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        

        <TabsContent value="notes" className="mt-6">
          <div className="space-y-6">
            {/* Activity Log Section */}
            <div className="remodra-card p-6">
              <div className="flex justify-between items-center mb-4">
                <h5 className="font-medium text-green-500 flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Client Activity Log
                </h5>
                <Button 
                  size="sm"
                  className="remodra-button"
                  onClick={() => setIsAddingNote(true)}
                  disabled={isAddingNote}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Note
                </Button>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Track calls, meetings, and client interactions. Notes are permanent and cannot be edited or deleted.
              </p>
              
              {/* Add Note Form */}
              {isAddingNote && (
                <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                        Note Type
                      </label>
                      <select
                        value={noteType}
                        onChange={(e) => setNoteType(e.target.value)}
                        className="remodra-input w-full"
                      >
                        <option value="general">📝 General</option>
                        <option value="call">📞 Phone Call</option>
                        <option value="meeting">🤝 Meeting</option>
                        <option value="email">✉️ Email</option>
                        <option value="visit">🏠 Site Visit</option>
                        <option value="issue">⚠️ Issue/Concern</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                        Note Details
                      </label>
                      <Textarea
                        value={newNoteText}
                        onChange={(e) => setNewNoteText(e.target.value)}
                        placeholder="Example: Called client, left voicemail about estimate approval..."
                        rows={4}
                        className="remodra-input"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleAddNote}
                        disabled={addNoteMutation.isPending || !newNoteText.trim()}
                        className="remodra-button"
                      >
                        {addNoteMutation.isPending ? 'Saving...' : 'Save Note'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setIsAddingNote(false);
                          setNewNoteText('');
                          setNoteType('general');
                        }}
                        disabled={addNoteMutation.isPending}
                        className="remodra-button-outline"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes List */}
              <div className="space-y-3">
                {isLoadingNotes ? (
                  <div className="text-center py-8 text-slate-400">
                    Loading notes...
                  </div>
                ) : clientNotes.length > 0 ? (
                  clientNotes.map((note: any) => {
                    const noteTypeEmoji = {
                      general: '📝',
                      call: '📞',
                      meeting: '🤝',
                      email: '✉️',
                      visit: '🏠',
                      issue: '⚠️'
                    }[note.noteType] || '📝';

                    return (
                      <div 
                        key={note.id}
                        className="p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{noteTypeEmoji}</span>
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              {new Date(note.createdAt).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </span>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {note.userName}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap">
                          {note.noteText}
                        </p>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg">
                    <FileText className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-600 dark:text-slate-400 mb-2">No activity notes yet</p>
                    <p className="text-sm text-slate-500 dark:text-slate-500">
                      Click "Add Note" to record calls, meetings, or other client interactions
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Cancellation History Section */}
            <div className="remodra-card p-6">
              <h5 className="font-medium text-green-500 mb-4 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-orange-500" />
                Cancellation History
              </h5>
              <p className="text-sm text-slate-400 mb-4">
                Record of cancelled appointments and events with reasons for cancellation.
              </p>
              <div className="bg-orange-600/20 border border-orange-500/30 p-4 rounded-lg text-sm">
                {client.cancellationHistory ? (
                  <div className="space-y-1">
                    {client.cancellationHistory.split('\n').map((entry, index) => (
                      <p key={index} className="text-orange-300">{entry}</p>
                    ))}
                  </div>
                ) : (
                  <p className="text-orange-400 italic">No cancellation history for this client.</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="portal">
          <div className="space-y-6">
            <h5 className="font-medium text-green-500 mb-4">Client Portal</h5>
            
            <Card className="remodra-card">
              <CardHeader className="bg-slate-200 dark:bg-slate-800 rounded-t-lg border-b border-slate-300 dark:border-slate-600">
                <CardTitle className="text-base flex items-center gap-2 text-green-500">
                  <Share2 className="h-5 w-5" />
                  Share access with client
                </CardTitle>
                <CardDescription className="text-slate-600 dark:text-slate-300">
                  Provide the client with the following link so they can access their personalized portal
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    <p className="flex items-center gap-2 mb-2">
                      <Shield className="h-4 w-4 text-green-500" />
                      <span>Secure access with time-limited token</span>
                    </p>
                    <p className="text-xs text-slate-500">
                      Click "Generate Link" to create a secure portal URL that expires in 30 days
                    </p>
                  </div>
                  {showPortalUrl && portalUrl && (
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-600 dark:text-slate-400">Portal Link:</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          ref={portalLinkRef}
                          value={portalUrl}
                          readOnly
                          className="flex-1 text-sm bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-600"
                          onClick={(e) => {
                            // Select all text when clicked
                            (e.target as HTMLInputElement).select();
                            // Try to copy on mobile
                            if (navigator.clipboard) {
                              navigator.clipboard.writeText(portalUrl).catch(() => {
                                // Fallback: just select the text
                              });
                            }
                          }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (portalLinkRef.current) {
                              portalLinkRef.current.select();
                              if (navigator.clipboard) {
                                navigator.clipboard.writeText(portalUrl).then(() => {
                                  toast({
                                    title: "Copied!",
                                    description: "Link copied to clipboard.",
                                  });
                                }).catch(() => {
                                  toast({
                                    title: "Selected",
                                    description: "Link selected. Use your device's copy function.",
                                  });
                                });
                              }
                            }
                          }}
                          className="border-slate-300 dark:border-slate-600"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Tap the link field to select it, or use the copy button
                      </p>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={copyPortalLink} 
                      className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 min-h-[44px] sm:min-h-0"
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Generate & Copy Link
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={openClientPortal}
                      className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 min-h-[44px] sm:min-h-0"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Preview Portal
                    </Button>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between border-t border-slate-300 dark:border-slate-600 pt-4 bg-slate-100 dark:bg-slate-800/50">
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  <p className="flex items-center gap-1">
                    <ClipboardCheck className="h-4 w-4 text-green-500" />
                    <span>Access to estimates and invoices</span>
                  </p>
                  <p className="flex items-center gap-1">
                    <FileSignature className="h-4 w-4 text-green-500" />
                    <span>Digital document signing</span>
                  </p>
                </div>
                <Button onClick={openClientPortal} className="remodra-button flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Open Client Portal
                </Button>
              </CardFooter>
            </Card>
            
            <div className="text-sm text-slate-400 p-4 border border-blue-500/30 bg-blue-600/20 rounded-lg">
              <p className="font-medium text-blue-400 mb-2">Tip:</p>
              <p>Share this link with your client so they can access their project information, view and approve estimates, sign invoices, and track their work in progress.</p>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      <div className="flex space-x-3 mt-6">
        <Button className="remodra-button flex items-center" onClick={onEdit}>
          <Edit className="h-4 w-4 mr-2" />
          Edit Client
        </Button>
        <Button variant="outline" className="remodra-button-outline flex items-center" onClick={onNewEstimate}>
          <FileText className="h-4 w-4 mr-2" />
          New Estimate
        </Button>
        <Button 
          variant="outline" 
          className="flex items-center text-red-400 hover:text-red-300 hover:bg-red-600/20 border-red-500/50"
          onClick={handleDeleteClick}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </Button>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {client.firstName} {client.lastName}? 
              This action cannot be undone and will remove all data associated with this client.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              type="button"
              variant="destructive"
              onClick={handleConfirmDelete}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Project Form Dialog */}
      <Dialog open={isProjectFormOpen} onOpenChange={setIsProjectFormOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedProject ? "Edit Project" : "New Project"}
            </DialogTitle>
            <DialogDescription>
              {selectedProject 
                ? "Update the project information"
                : "Enter the details for the new project"}
            </DialogDescription>
          </DialogHeader>
          <ProjectForm
            project={selectedProject || undefined}
            clientId={client.id}
            onSubmit={handleProjectFormSubmit}
            isSubmitting={isCreating || isUpdating}
            onCancel={() => setIsProjectFormOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}